import React from "react";
import Header from "parts/Header";
import Footer from "parts/Footer";
import {
  Fade,
  Slide,
  Zoom,
  Roll,
  Bounce,
  AttentionSeeker,
} from "react-awesome-reveal";
import Discuss from "parts/Discuss";

const CompetitiveAdvantagesPage = () => (
  <>
    <Header />

    {/* Hero Section with Dynamic Background */}
    <div className="relative flex items-center min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Animated Geometric Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-64 h-64 rounded-full top-1/4 left-1/4 bg-gradient-to-r from-purple-400 to-pink-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
        <div className="absolute delay-1000 rounded-full top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
        <div className="absolute delay-500 transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-80 h-80 bg-gradient-to-r from-emerald-400 to-teal-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-4 h-4 bg-white rounded-full top-20 left-20 opacity-60 animate-bounce" />
        <div className="absolute w-6 h-6 delay-300 bg-purple-300 rounded-full top-40 right-32 opacity-40 animate-bounce" />
        <div className="absolute w-3 h-3 delay-700 rounded-full opacity-50 bottom-32 left-1/3 bg-cyan-300 animate-bounce" />
        <div className="absolute w-5 h-5 delay-1000 bg-pink-300 rounded-full bottom-20 right-20 opacity-60 animate-bounce" />
      </div>

      <div className="container relative z-10 px-6 mx-auto">
        <div className="text-center text-white">
          <Roll triggerOnce>
            <div className="inline-block mb-8">
              <div className="flex items-center justify-center w-24 h-24 mx-auto text-4xl transition-transform duration-500 transform bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl rotate-12 hover:rotate-0">
                🏆
              </div>
            </div>
          </Roll>

          <Fade direction="up" triggerOnce duration={1000}>
            <h1 className="mb-6 text-6xl font-black text-transparent md:text-8xl bg-gradient-to-r from-white via-purple-200 to-cyan-200 bg-clip-text">
              COMPETITIVE
            </h1>
          </Fade>

          <Fade direction="up" delay={300} triggerOnce>
            <h2 className="mb-8 text-4xl font-light text-purple-200 md:text-5xl">
              Advantages
            </h2>
          </Fade>

          <Fade direction="up" delay={600} triggerOnce>
            <p className="max-w-4xl mx-auto mb-12 text-xl leading-relaxed text-gray-300 md:text-2xl">
              Delivering strategic excellence through flexible innovation,
              unwavering expertise, and long-term partnership commitment
            </p>
          </Fade>

          <Bounce delay={1000} triggerOnce>
            <div className="inline-flex items-center px-6 py-3 space-x-2 border rounded-full bg-white/10 backdrop-blur-md border-white/20">
              <span className="text-sm font-medium">
                Scroll to explore our edge
              </span>
              <div className="w-4 h-4 transform rotate-45 border-b-2 border-r-2 border-white animate-bounce" />
            </div>
          </Bounce>
        </div>
      </div>
    </div>

    {/* Main Advantages Section */}
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container px-6 mx-auto">
        {/* Section Header */}
        <Fade direction="up" triggerOnce>
          <div className="mb-20 text-center">
            <h3 className="mb-6 text-5xl font-bold text-gray-800">
              What Sets Us Apart
            </h3>
            <div className="w-24 h-1 mx-auto mb-8 bg-gradient-to-r from-purple-500 to-cyan-500" />
            <p className="max-w-3xl mx-auto text-xl text-gray-600">
              Our unique combination of innovation, expertise, and strategic
              positioning creates unmatched value for our clients
            </p>
          </div>
        </Fade>

        {/* Advantages Grid */}
        <div className="grid gap-12 mb-20 lg:grid-cols-2">
          {[
            {
              title: "Flexible Service Architecture",
              icon: "⚡",
              description:
                "Adaptive solutions that scale with your business needs, offering customizable service packages that evolve as you grow.",
              features: [
                "Modular Service Design",
                "Scalable Solutions",
                "Custom Integration",
              ],
              gradient: "from-blue-500 to-cyan-500",
              delay: 0,
            },
            {
              title: "Long-term Value Partnership",
              icon: "🎯",
              description:
                "Strategic relationships built on sustainable growth principles, focusing on enduring success rather than quick wins.",
              features: [
                "Strategic Planning",
                "Sustainable Growth",
                "Partnership Focus",
              ],
              gradient: "from-purple-500 to-pink-500",
              delay: 200,
            },
            {
              title: "Expert Professional Team",
              icon: "👥",
              description:
                "Seasoned professionals with deep industry knowledge and proven track records across diverse business sectors.",
              features: [
                "Industry Expertise",
                "Proven Track Record",
                "Diverse Specializations",
              ],
              gradient: "from-emerald-500 to-teal-500",
              delay: 400,
            },
            {
              title: "Innovation & Compliance",
              icon: "🔒",
              description:
                "Cutting-edge solutions that meet the highest compliance standards while driving innovation in your industry.",
              features: [
                "Regulatory Excellence",
                "Innovation Focus",
                "Risk Management",
              ],
              gradient: "from-orange-500 to-red-500",
              delay: 600,
            },
          ].map((advantage, index) => (
            <Slide
              key={advantage.title}
              direction={index % 2 === 0 ? "left" : "right"}
              delay={advantage.delay}
              triggerOnce
            >
              <div className="relative p-8 overflow-hidden transition-all duration-500 transform bg-white border border-gray-100 shadow-xl group rounded-3xl hover:shadow-2xl hover:-translate-y-4">
                {/* Gradient Background Effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${advantage.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                />

                {/* Icon */}
                <div className="relative z-10">
                  <div className="mb-6 text-6xl transition-transform duration-300 transform group-hover:scale-110">
                    {advantage.icon}
                  </div>

                  <h4 className="mb-4 text-2xl font-bold text-gray-800 group-hover:text-gray-900">
                    {advantage.title}
                  </h4>

                  <p className="mb-6 leading-relaxed text-gray-600 group-hover:text-gray-700">
                    {advantage.description}
                  </p>

                  {/* Feature Pills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {advantage.features.map((feature, idx) => (
                      <span
                        key={feature}
                        className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${advantage.gradient} text-white transform group-hover:scale-105 transition-transform duration-300`}
                        style={{ animationDelay: `${idx * 100}ms` }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Bottom Accent Line */}
                  <div
                    className={`w-full h-1 bg-gradient-to-r ${advantage.gradient} rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}
                  />
                </div>
              </div>
            </Slide>
          ))}
        </div>

        {/* Texas Presence Highlight */}
        <Zoom triggerOnce>
          <div className="relative p-12 mb-20 overflow-hidden text-white bg-gradient-to-r from-blue-900 to-purple-900 rounded-3xl">
            <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32 rounded-full bg-white/10" />
            <div className="absolute bottom-0 left-0 w-48 h-48 transform -translate-x-24 translate-y-24 rounded-full bg-white/5" />

            <div className="relative z-10 text-center">
              <div className="mb-6 text-6xl">🌟</div>
              <h3 className="mb-6 text-4xl font-bold">
                Strong Texas Market Presence
              </h3>
              <p className="max-w-3xl mx-auto mb-8 text-xl text-blue-100">
                Deep-rooted expertise in the Texas business landscape with
                extensive local networks, regulatory knowledge, and market
                insights that give our clients a distinct regional advantage.
              </p>
              <div className="grid gap-6 md:grid-cols-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-300">15+</div>
                  <div className="text-sm text-blue-200">Years in Texas</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300">500+</div>
                  <div className="text-sm text-blue-200">Local Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-pink-300">98%</div>
                  <div className="text-sm text-blue-200">
                    Client Satisfaction
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Zoom>

        {/* Featured Image Section */}
        <Fade direction="up" triggerOnce>
          <div className="max-w-6xl mx-auto mb-20">
            <div className="relative overflow-hidden shadow-2xl rounded-3xl group">
              <img
                src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=1200&q=80"
                alt="Competitive Advantages in Action"
                className="object-cover w-full transition-transform duration-700 transform h-96 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 group-hover:opacity-100" />

              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h4 className="mb-4 text-3xl font-bold">
                  Excellence in Action
                </h4>
                <p className="max-w-3xl text-lg text-gray-200">
                  Our robust portfolio showcases successful implementations
                  across diverse industries, demonstrating our ability to
                  deliver scalable solutions that drive measurable results.
                </p>
              </div>
            </div>
          </div>
        </Fade>

        {/* Call to Action */}
        <Fade direction="up" triggerOnce>
          <div className="text-center">
            <AttentionSeeker effect="pulse" delay={1000} triggerOnce>
              <div className="relative inline-block">
                <div className="absolute inset-0 rounded-full opacity-75 bg-gradient-to-r from-purple-500 to-cyan-500 blur-lg animate-pulse" />
                <button
                  type="button"
                  className="relative px-12 py-6 text-lg font-bold text-white transition-all duration-300 transform rounded-full shadow-2xl bg-gradient-to-r from-purple-600 to-cyan-600 hover:scale-105"
                >
                  Experience Our Competitive Edge
                </button>
              </div>
            </AttentionSeeker>

            <Fade direction="up" delay={500} triggerOnce>
              <p className="max-w-2xl mx-auto mt-6 text-gray-600">
                Ready to leverage our competitive advantages for your business
                success? Let&apos;s discuss how our unique approach can
                accelerate your growth.
              </p>
            </Fade>
          </div>
        </Fade>
      </div>
    </div>

    <Discuss />
    <Footer />
  </>
);

export default CompetitiveAdvantagesPage;
